/**
 * Lookup tables cho hai tool đơn giản còn lại trong Phase 2:
 *  - tuoi-ket-hon  : tuổi nên kết hôn theo Cung Mệnh
 *  - so-mang-60-tuoi : 60 hoa giáp + lá số tóm tắt theo cặp can-chi
 *
 * Personalization: `fateSummary(year, opts?)` accepts `{ gender, topic }`:
 *  - gender → derive zodiac compat (3 chi tam hợp / 4 chi tứ hành xung)
 *  - topic  → return contextual guidance per element × topic (5 × 5 = 25)
 */

import { canChiOfYear } from "@/lib/compat";
import { elementFromYear } from "@/lib/feng-shui/colors";
import { isKimLau, isHoangOc, isTamTai } from "@/lib/feng-shui/year-pick";

/**
 * Tuổi tốt cho hôn nhân — dân gian Việt Nam có quan niệm:
 *  - Nam nên cưới ở các năm chẵn / năm tuổi không phạm Kim Lâu
 *  - Nữ tránh các năm tuổi 1, 3, 6, 8 mod 9 (tương tự Kim Lâu)
 * Tool này chỉ ra 5 tuổi nên cưới sớm nhất từ tuổi 22 (nam) / 20 (nữ).
 */
export type MarriageYear = {
  age: number;
  calendarYear: number;
  recommended: boolean;
  reason: string;
  details: {
    kimLau: { ok: boolean; type?: string };
    hoangOc: { ok: boolean; type: string };
    tamTai: { ok: boolean; triad?: string };
  };
};

export function suggestMarriageYears(birthYear: number, gender: "nam" | "nu"): MarriageYear[] {
  const startAge = gender === "nam" ? 22 : 20;
  const out: MarriageYear[] = [];
  for (let age = startAge; age <= startAge + 14; age++) {
    const calendarYear = birthYear + age;
    const ageMu = age + 1;
    const kl = isKimLau(birthYear, calendarYear);
    const ho = isHoangOc(birthYear, calendarYear);
    const tt = isTamTai(birthYear, calendarYear);
    const recommended = kl.ok && ho.ok && tt.ok;
    const reasons: string[] = [];
    if (!kl.ok) reasons.push(`phạm ${kl.type}`);
    if (!ho.ok) reasons.push(`Hoang Ốc ${ho.type}`);
    if (!tt.ok) reasons.push(`Tam Tai (${tt.triad})`);
    out.push({
      age,
      calendarYear,
      recommended,
      reason: recommended
        ? `Tuổi mụ ${ageMu}, tốt cả 3 yếu tố Kim Lâu / Hoang Ốc / Tam Tai.`
        : `Tuổi mụ ${ageMu}, ${reasons.join(", ")}.`,
      details: {
        kimLau: { ok: kl.ok, type: kl.type },
        hoangOc: { ok: ho.ok, type: ho.type ?? "" },
        tamTai: { ok: tt.ok, triad: tt.triad },
      },
    });
  }
  return out;
}

/**
 * Partner compatibility — kiểm tra tuổi đối phương theo 3 tiêu chí truyền thống:
 *  1. Tam hợp / Tứ hành xung (chi)
 *  2. Ngũ hành mệnh (sinh / khắc / bình hoà)
 *  3. Cách biệt số tuổi (kiêng cách 3, 7 — "tam thất")
 */
export type PartnerCompat = {
  partnerYear: number;
  ageGap: number;
  branch: { self: string; partner: string };
  element: { self: string; partner: string };
  zodiacRelation: "tam-hop" | "tu-hanh-xung" | "neutral";
  elementRelation: "sinh-cho" | "duoc-sinh" | "binh-hoa" | "khac-cho" | "bi-khac";
  tamThat: boolean;
  score: number; // 0-100
  rating: "rất hợp" | "khá hợp" | "tạm chấp nhận" | "không thực sự hợp";
  notes: string[];
};

const TAM_HOP_PARTNER: Record<string, string[]> = {
  Thân: ["Tý", "Thìn"],
  Tý: ["Thân", "Thìn"],
  Thìn: ["Thân", "Tý"],
  Tỵ: ["Dậu", "Sửu"],
  Dậu: ["Tỵ", "Sửu"],
  Sửu: ["Tỵ", "Dậu"],
  Dần: ["Ngọ", "Tuất"],
  Ngọ: ["Dần", "Tuất"],
  Tuất: ["Dần", "Ngọ"],
  Hợi: ["Mão", "Mùi"],
  Mão: ["Hợi", "Mùi"],
  Mùi: ["Hợi", "Mão"],
};

const TU_HANH_XUNG_PARTNER: Record<string, string> = {
  Tý: "Ngọ",
  Ngọ: "Tý",
  Sửu: "Mùi",
  Mùi: "Sửu",
  Dần: "Thân",
  Thân: "Dần",
  Mão: "Dậu",
  Dậu: "Mão",
  Thìn: "Tuất",
  Tuất: "Thìn",
  Tỵ: "Hợi",
  Hợi: "Tỵ",
};

// Element generation chain: Kim sinh Thuỷ, Thuỷ sinh Mộc, Mộc sinh Hoả, Hoả sinh Thổ, Thổ sinh Kim
const SINH: Record<string, string> = {
  Kim: "Thuỷ",
  Thuỷ: "Mộc",
  Mộc: "Hoả",
  Hoả: "Thổ",
  Thổ: "Kim",
};

// Element control: Kim khắc Mộc, Mộc khắc Thổ, Thổ khắc Thuỷ, Thuỷ khắc Hoả, Hoả khắc Kim
const KHAC: Record<string, string> = {
  Kim: "Mộc",
  Mộc: "Thổ",
  Thổ: "Thuỷ",
  Thuỷ: "Hoả",
  Hoả: "Kim",
};

export function partnerCompat(selfYear: number, partnerYear: number): PartnerCompat {
  const selfCC = canChiOfYear(selfYear);
  const partnerCC = canChiOfYear(partnerYear);
  const selfEl = elementFromYear(selfYear);
  const partnerEl = elementFromYear(partnerYear);

  // Zodiac relation
  let zodiacRelation: PartnerCompat["zodiacRelation"] = "neutral";
  if (TAM_HOP_PARTNER[selfCC.branch]?.includes(partnerCC.branch)) {
    zodiacRelation = "tam-hop";
  } else if (TU_HANH_XUNG_PARTNER[selfCC.branch] === partnerCC.branch) {
    zodiacRelation = "tu-hanh-xung";
  }

  // Element relation
  let elementRelation: PartnerCompat["elementRelation"] = "binh-hoa";
  if (selfEl === partnerEl) {
    elementRelation = "binh-hoa";
  } else if (SINH[selfEl] === partnerEl) {
    elementRelation = "sinh-cho"; // self sinh partner
  } else if (SINH[partnerEl] === selfEl) {
    elementRelation = "duoc-sinh"; // partner sinh self
  } else if (KHAC[selfEl] === partnerEl) {
    elementRelation = "khac-cho"; // self khắc partner
  } else if (KHAC[partnerEl] === selfEl) {
    elementRelation = "bi-khac"; // partner khắc self
  }

  // Age gap
  const ageGap = Math.abs(selfYear - partnerYear);
  const tamThat = ageGap === 3 || ageGap === 7;

  // Score
  let score = 50;
  const notes: string[] = [];

  if (zodiacRelation === "tam-hop") {
    score += 25;
    notes.push(`Chi ${selfCC.branch} và ${partnerCC.branch} thuộc Tam hợp — hỗ trợ nhau lâu dài.`);
  } else if (zodiacRelation === "tu-hanh-xung") {
    score -= 30;
    notes.push(
      `Chi ${selfCC.branch} và ${partnerCC.branch} đối xung 180° — Tứ hành xung, cần cẩn trọng.`
    );
  } else {
    notes.push(`Chi ${selfCC.branch} và ${partnerCC.branch} không xung khắc rõ rệt.`);
  }

  if (elementRelation === "duoc-sinh") {
    score += 20;
    notes.push(`Mệnh ${partnerEl} sinh Mệnh ${selfEl} — đối phương mang lại may mắn cho bạn.`);
  } else if (elementRelation === "sinh-cho") {
    score += 15;
    notes.push(`Mệnh ${selfEl} sinh Mệnh ${partnerEl} — bạn nâng đỡ đối phương phát triển.`);
  } else if (elementRelation === "binh-hoa") {
    score += 5;
    notes.push(`Hai mệnh đều ${selfEl} — bình hoà, không xung không sinh.`);
  } else if (elementRelation === "bi-khac") {
    score -= 20;
    notes.push(`Mệnh ${partnerEl} khắc Mệnh ${selfEl} — đối phương dễ áp đảo bạn về tính cách.`);
  } else if (elementRelation === "khac-cho") {
    score -= 15;
    notes.push(`Mệnh ${selfEl} khắc Mệnh ${partnerEl} — bạn dễ áp đảo đối phương.`);
  }

  if (tamThat) {
    score -= 15;
    notes.push(
      `Cách ${ageGap} tuổi — dân gian kiêng "tam thất" (cách 3, 7), tin rằng dễ trắc trở. Nhiều cặp đôi vẫn vượt qua được nếu hiểu nhau.`
    );
  } else if (ageGap === 0) {
    notes.push(`Cùng tuổi — cần thêm phân tích cá nhân vì có thể trùng cả mệnh và chi.`);
  }

  score = Math.max(0, Math.min(100, score));
  let rating: PartnerCompat["rating"] = "tạm chấp nhận";
  if (score >= 80) rating = "rất hợp";
  else if (score >= 65) rating = "khá hợp";
  else if (score >= 45) rating = "tạm chấp nhận";
  else rating = "không thực sự hợp";

  return {
    partnerYear,
    ageGap,
    branch: { self: selfCC.branch, partner: partnerCC.branch },
    element: { self: selfEl, partner: partnerEl },
    zodiacRelation,
    elementRelation,
    tamThat,
    score,
    rating,
    notes,
  };
}

/**
 * Sơ Mệnh — bảng 60 hoa giáp tóm tắt.
 *
 * Mỗi cặp Can-Chi có một câu mệnh ngắn (theo bài thơ Nạp Âm). Đây là
 * version giản lược — đủ để giới thiệu tổng quát Mệnh nạp âm.
 */
export type FateSummary = {
  year: number;
  stem: string;
  branch: string;
  napAm: string; // Tên Mệnh (vd "Hải Trung Kim")
  element: string;
  description: string;
  topic?: {
    key: FateTopic;
    label: string;
    tips: string[];
  };
  zodiac?: {
    branch: string;
    tamHop: string[];
    tuHanhXung: string[];
    gender: "Nam" | "Nữ";
  };
};

const NAP_AM_NAMES: Record<string, string> = {
  "Giáp Tý": "Hải Trung Kim",
  "Ất Sửu": "Hải Trung Kim",
  "Bính Dần": "Lư Trung Hỏa",
  "Đinh Mão": "Lư Trung Hỏa",
  "Mậu Thìn": "Đại Lâm Mộc",
  "Kỷ Tỵ": "Đại Lâm Mộc",
  "Canh Ngọ": "Lộ Bàng Thổ",
  "Tân Mùi": "Lộ Bàng Thổ",
  "Nhâm Thân": "Kiếm Phong Kim",
  "Quý Dậu": "Kiếm Phong Kim",
  "Giáp Tuất": "Sơn Đầu Hỏa",
  "Ất Hợi": "Sơn Đầu Hỏa",
  "Bính Tý": "Giản Hạ Thuỷ",
  "Đinh Sửu": "Giản Hạ Thuỷ",
  "Mậu Dần": "Thành Đầu Thổ",
  "Kỷ Mão": "Thành Đầu Thổ",
  "Canh Thìn": "Bạch Lạp Kim",
  "Tân Tỵ": "Bạch Lạp Kim",
  "Nhâm Ngọ": "Dương Liễu Mộc",
  "Quý Mùi": "Dương Liễu Mộc",
  "Giáp Thân": "Tuyền Trung Thuỷ",
  "Ất Dậu": "Tuyền Trung Thuỷ",
  "Bính Tuất": "Ốc Thượng Thổ",
  "Đinh Hợi": "Ốc Thượng Thổ",
  "Mậu Tý": "Tích Lịch Hỏa",
  "Kỷ Sửu": "Tích Lịch Hỏa",
  "Canh Dần": "Tùng Bách Mộc",
  "Tân Mão": "Tùng Bách Mộc",
  "Nhâm Thìn": "Trường Lưu Thuỷ",
  "Quý Tỵ": "Trường Lưu Thuỷ",
  "Giáp Ngọ": "Sa Trung Kim",
  "Ất Mùi": "Sa Trung Kim",
  "Bính Thân": "Sơn Hạ Hỏa",
  "Đinh Dậu": "Sơn Hạ Hỏa",
  "Mậu Tuất": "Bình Địa Mộc",
  "Kỷ Hợi": "Bình Địa Mộc",
  "Canh Tý": "Bích Thượng Thổ",
  "Tân Sửu": "Bích Thượng Thổ",
  "Nhâm Dần": "Kim Bạch Kim",
  "Quý Mão": "Kim Bạch Kim",
  "Giáp Thìn": "Phú Đăng Hỏa",
  "Ất Tỵ": "Phú Đăng Hỏa",
  "Bính Ngọ": "Thiên Hà Thuỷ",
  "Đinh Mùi": "Thiên Hà Thuỷ",
  "Mậu Thân": "Đại Trạch Thổ",
  "Kỷ Dậu": "Đại Trạch Thổ",
  "Canh Tuất": "Thoa Xuyến Kim",
  "Tân Hợi": "Thoa Xuyến Kim",
  "Nhâm Tý": "Tang Đố Mộc",
  "Quý Sửu": "Tang Đố Mộc",
  "Giáp Dần": "Đại Khê Thuỷ",
  "Ất Mão": "Đại Khê Thuỷ",
  "Bính Thìn": "Sa Trung Thổ",
  "Đinh Tỵ": "Sa Trung Thổ",
  "Mậu Ngọ": "Thiên Thượng Hỏa",
  "Kỷ Mùi": "Thiên Thượng Hỏa",
  "Canh Thân": "Thạch Lựu Mộc",
  "Tân Dậu": "Thạch Lựu Mộc",
  "Nhâm Tuất": "Đại Hải Thuỷ",
  "Quý Hợi": "Đại Hải Thuỷ",
};

const NAP_AM_DESC: Record<string, string> = {
  "Hải Trung Kim":
    "Vàng trong biển — tiềm năng lớn nhưng cần thời gian khai phá. Người mệnh này thường khởi sắc giai đoạn trung niên.",
  "Lư Trung Hỏa":
    "Lửa trong lò — sức mạnh tập trung. Tính cách quyết liệt, làm việc gì cũng đến cùng.",
  "Đại Lâm Mộc": "Cây trong rừng lớn — sức sống dồi dào, có khả năng dẫn dắt cộng đồng.",
  "Lộ Bàng Thổ": "Đất ven đường — sức chịu đựng cao, gánh trách nhiệm gia đình hoặc tổ chức.",
  "Kiếm Phong Kim": "Vàng đầu mũi kiếm — sắc bén, quyết đoán. Hợp lãnh đạo, chỉ huy.",
  "Sơn Đầu Hỏa": "Lửa đỉnh núi — bùng phát mạnh nhưng cần kiên trì để không tắt sớm.",
  "Giản Hạ Thuỷ": "Nước khe núi — sâu nhưng tĩnh. Người trí tuệ, ít nói nhưng làm nhiều.",
  "Thành Đầu Thổ": "Đất đầu thành — vững chãi và đáng tin. Giữ vai trò trung tâm trong tập thể.",
  "Bạch Lạp Kim": "Vàng nến trắng — đẹp và quý nhưng cần được mài giũa. Phát triển muộn.",
  "Dương Liễu Mộc": "Cây dương liễu — mềm mại, uyển chuyển. Khéo léo trong giao tiếp.",
  "Tuyền Trung Thuỷ": "Nước trong giếng — trong sạch, nuôi dưỡng người khác.",
  "Ốc Thượng Thổ": "Đất trên mái nhà — bảo vệ gia đình. Tính trách nhiệm cao.",
  "Tích Lịch Hỏa": "Lửa sấm sét — bộc phát mạnh, có sức ảnh hưởng lớn nhưng đôi khi quá đột ngột.",
  "Tùng Bách Mộc": "Cây tùng bách — kiên cường, sống lâu, không chịu khuất phục nghịch cảnh.",
  "Trường Lưu Thuỷ": "Nước chảy dài — bền bỉ và rộng. Thành công đến từ sự kiên trì hằng ngày.",
  "Sa Trung Kim":
    "Vàng trong cát — quý hiếm, cần người tinh tường mới phát hiện. Thường có quý nhân.",
  "Sơn Hạ Hỏa": "Lửa chân núi — ấm áp, ổn định. Mang lại cảm giác an toàn cho gia đình.",
  "Bình Địa Mộc": "Cây trên đồng bằng — phát triển rộng, có sức ảnh hưởng đến nhiều người.",
  "Bích Thượng Thổ":
    "Đất trên vách — chống đỡ kết cấu. Người mệnh này thường giữ trụ cột gia đình.",
  "Kim Bạch Kim": "Vàng trắng tinh chất — tinh khiết, có giá trị cao. Phù hợp công việc trí tuệ.",
  "Phú Đăng Hỏa": "Lửa đèn dầu — soi đường. Người dạy học, tư vấn, hướng dẫn.",
  "Thiên Hà Thuỷ": "Nước sông Ngân — cao xa, lý tưởng. Có tầm nhìn vượt thời gian.",
  "Đại Trạch Thổ": "Đất đầm lớn — màu mỡ, nuôi dưỡng. Người mệnh này thu hút quý nhân.",
  "Thoa Xuyến Kim": "Vàng làm trâm cài — đẹp và tinh tế. Phù hợp nghệ thuật, thẩm mỹ.",
  "Tang Đố Mộc": "Cây dâu tằm — gắn với gia đình, lao động chăm chỉ.",
  "Đại Khê Thuỷ": "Nước suối lớn — chảy mạnh, không gì cản. Người dám nghĩ dám làm.",
  "Sa Trung Thổ": "Đất pha cát — linh hoạt, dễ thích nghi với hoàn cảnh mới.",
  "Thiên Thượng Hỏa": "Lửa trên trời (mặt trời) — chiếu sáng tất cả. Lãnh đạo bẩm sinh.",
  "Thạch Lựu Mộc": "Cây lựu trên đá — sinh tồn nơi khắc nghiệt. Bản lĩnh phi thường.",
  "Đại Hải Thuỷ": "Nước biển lớn — bao dung, sâu rộng. Người độ lượng với mọi người.",
};

export function fateSummary(
  year: number,
  opts?: { gender?: "Nam" | "Nữ"; topic?: FateTopic }
): FateSummary {
  const cc = canChiOfYear(year);
  const key = `${cc.stem} ${cc.branch}`;
  const napAm = NAP_AM_NAMES[key] ?? "";
  const element = elementFromYear(year);
  const result: FateSummary = {
    year,
    stem: cc.stem,
    branch: cc.branch,
    napAm,
    element,
    description: NAP_AM_DESC[napAm] ?? "Chưa có mô tả cho mệnh này.",
  };

  if (opts?.topic) {
    const elementKey = element as keyof typeof TOPIC_GUIDE;
    const guide = TOPIC_GUIDE[elementKey]?.[opts.topic];
    if (guide) {
      result.topic = {
        key: opts.topic,
        label: TOPIC_LABELS[opts.topic],
        tips: guide,
      };
    }
  }

  if (opts?.gender) {
    result.zodiac = {
      branch: cc.branch,
      tamHop: TAM_HOP[cc.branch] ?? [],
      tuHanhXung: TU_HANH_XUNG[cc.branch] ?? [],
      gender: opts.gender,
    };
  }

  return result;
}

// =============================================================
// Personalization helpers
// =============================================================

export type FateTopic = "career" | "love" | "family" | "health" | "finance";

export const TOPIC_LABELS: Record<FateTopic, string> = {
  career: "Sự nghiệp",
  love: "Tình duyên",
  family: "Gia đạo",
  health: "Sức khoẻ",
  finance: "Tài lộc",
};

/**
 * Tam hợp + Tứ hành xung — quan hệ giữa 12 địa chi.
 *  - Tam hợp: 4 nhóm 3 chi hỗ trợ nhau (Thân-Tý-Thìn, Tỵ-Dậu-Sửu, Dần-Ngọ-Tuất, Hợi-Mão-Mùi)
 *  - Tứ hành xung: 3 cặp đối xứng 180° trên vòng 12 chi (Tý-Ngọ, Sửu-Mùi, Dần-Thân, Mão-Dậu, Thìn-Tuất, Tỵ-Hợi)
 *
 * Đây là kiến thức hợp tuổi cơ bản, dùng để gợi ý "tuổi hợp / kỵ" cho năm sinh.
 */
const TAM_HOP: Record<string, string[]> = {
  Thân: ["Tý", "Thìn"],
  Tý: ["Thân", "Thìn"],
  Thìn: ["Thân", "Tý"],
  Tỵ: ["Dậu", "Sửu"],
  Dậu: ["Tỵ", "Sửu"],
  Sửu: ["Tỵ", "Dậu"],
  Dần: ["Ngọ", "Tuất"],
  Ngọ: ["Dần", "Tuất"],
  Tuất: ["Dần", "Ngọ"],
  Hợi: ["Mão", "Mùi"],
  Mão: ["Hợi", "Mùi"],
  Mùi: ["Hợi", "Mão"],
};

const TU_HANH_XUNG: Record<string, string[]> = {
  Tý: ["Ngọ"],
  Ngọ: ["Tý"],
  Sửu: ["Mùi"],
  Mùi: ["Sửu"],
  Dần: ["Thân"],
  Thân: ["Dần"],
  Mão: ["Dậu"],
  Dậu: ["Mão"],
  Thìn: ["Tuất"],
  Tuất: ["Thìn"],
  Tỵ: ["Hợi"],
  Hợi: ["Tỵ"],
};

/**
 * Topic guidance per element × topic (5 × 5 = 25 advice blocks).
 * Kim/Mộc/Thuỷ/Hoả/Thổ × career/love/family/health/finance.
 */
const TOPIC_GUIDE: Record<string, Record<FateTopic, string[]>> = {
  Kim: {
    career: [
      "Hợp ngành đòi quyết đoán: tài chính, lãnh đạo, kỹ thuật chính xác, luật pháp, quân đội.",
      "Sức bật mạnh nhất ở giai đoạn 30-45 tuổi, nên đầu tư xây danh tiếng giai đoạn này.",
      "Tránh nghề mềm dẻo cảm xúc thuần (nghệ thuật biểu diễn) — không tận dụng được sắc bén của Kim.",
    ],
    love: [
      "Cần đối tác hiểu sự thẳng thắn, không bóng gió — Mệnh Kim không giỏi giấu lòng.",
      "Hợp người mệnh Thổ (Thổ sinh Kim) hoặc cùng Kim. Kỵ Hoả (Hoả khắc Kim).",
      "Tránh lối yêu chiếm hữu — bản tính Kim đã sắc, thêm chiếm hữu dễ làm tổn thương người yêu.",
    ],
    family: [
      "Là trụ cột vững — gia đình thường tin tưởng giao quyết định lớn cho người mệnh Kim.",
      "Cần học mềm mỏng với con cái và người già — Kim sắc dễ làm con thấy xa cách.",
      "Phòng thờ và phòng khách nên đặt hướng Tây hoặc Tây Bắc (cung Kim).",
    ],
    health: [
      "Lưu ý hệ hô hấp (phổi, mũi xoang) — Kim đại diện cho phổi trong Đông y.",
      "Tránh môi trường khô lạnh kéo dài, không khí ô nhiễm.",
      "Tập luyện đều giúp Kim không bị 'cứng' — yoga, bơi lội, đi bộ thở sâu rất hợp.",
    ],
    finance: [
      "Mệnh Kim có duyên với tiền tệ kim loại, vàng, bất động sản, đầu tư an toàn dài hạn.",
      "Tránh đầu cơ ngắn hạn cảm xúc — Kim cần phân tích rõ ràng, không hợp đánh nhanh.",
      "Cất giữ tài sản tốt nhất ở phương Tây của nhà / két sắt.",
    ],
  },
  Mộc: {
    career: [
      "Hợp giáo dục, nông nghiệp, môi trường, y dược, thiết kế bền vững, tư vấn phát triển.",
      "Phát triển ổn theo thời gian — không bứt phá mà tích luỹ chắc chắn.",
      "Hợp làm việc nhóm và mentor — Mộc sinh trưởng, lan toả tri thức.",
    ],
    love: [
      "Cần đối tác cùng lý tưởng, có chí tiến thủ — Mộc luôn vươn lên, đứng yên là héo.",
      "Hợp người mệnh Thuỷ (Thuỷ sinh Mộc) hoặc cùng Mộc. Kỵ Kim (Kim khắc Mộc).",
      "Yêu kiểu chăm sóc sâu nhưng không kiểm soát — như tưới cây, đủ chứ không ngập.",
    ],
    family: [
      "Có khả năng nuôi dưỡng tinh thần thành viên gia đình — vai trò 'gốc cây'.",
      "Phòng làm việc và phòng học nên đặt hướng Đông hoặc Đông Nam (cung Mộc).",
      "Trồng cây xanh trong nhà giúp Mộc thêm vượng, đặc biệt là cây thân gỗ.",
    ],
    health: [
      "Lưu ý gan-mật và hệ thần kinh — Mộc trong Đông y ứng với gan.",
      "Tránh stress kéo dài, ngủ thiếu giấc — gan không được giải độc đêm.",
      "Đi bộ trong rừng / công viên giúp tái tạo Mộc khí; tập thể thao không nên quá nặng.",
    ],
    finance: [
      "Hợp đầu tư xanh, công ty bền vững dài hạn, tài sản sinh lời từ từ.",
      "Tránh tiêu xài theo cảm xúc — Mộc cần kế hoạch nhiều năm để tích luỹ.",
      "Két sắt / két tủ tốt nhất đặt hướng Đông Nam.",
    ],
  },
  Thuỷ: {
    career: [
      "Hợp truyền thông, vận tải, du lịch, nghiên cứu, công nghệ, ngoại giao, sáng tác.",
      "Linh hoạt và dễ thích nghi — phù hợp ngành thay đổi nhanh, đa nhiệm.",
      "Có tài thuyết phục và kết nối — phù hợp vai trò trung gian, hoà giải.",
    ],
    love: [
      "Cần đối tác có chiều sâu trí tuệ, không nông cạn — Thuỷ sâu lắng, cần được hiểu.",
      "Hợp người mệnh Kim (Kim sinh Thuỷ) hoặc cùng Thuỷ. Kỵ Thổ (Thổ khắc Thuỷ).",
      "Yêu kiểu chia sẻ ngọn ngành cảm xúc — nếu đối tác đóng kín, Thuỷ sẽ ngột ngạt.",
    ],
    family: [
      "Có khả năng giữ gia đạo êm ấm bằng sự uyển chuyển và chiều sâu lắng nghe.",
      "Phòng ngủ và phòng đọc nên đặt hướng Bắc (cung Thuỷ).",
      "Bể cá / hồ nước nhỏ trong nhà kích hoạt mạnh khí Thuỷ — đặt hướng Bắc.",
    ],
    health: [
      "Lưu ý hệ tiết niệu, thận, sinh sản — Thuỷ trong Đông y ứng với thận.",
      "Tránh thức khuya và uống nước lạnh quá nhiều — thận yếu nhanh.",
      "Bơi lội, thiền và yoga rất hợp; tập gym nặng nên giới hạn.",
    ],
    finance: [
      "Hợp đầu tư động: chứng khoán, ngoại hối, du lịch, dịch vụ.",
      "Có duyên 'tiền chảy đi tiền chảy lại' — cần kế hoạch giữ dòng, không tích trữ tĩnh.",
      "Két sắt nên đặt hướng Bắc; tránh để vàng (Kim sinh Thuỷ ngược lại làm Kim suy nếu không dùng đúng).",
    ],
  },
  Hoả: {
    career: [
      "Hợp giải trí, F&B, marketing, thời trang, nghệ thuật biểu diễn, sales, training.",
      "Năng lượng cao bùng phát — phù hợp dự án ngắn hạn cường độ cao hơn dự án 5-10 năm.",
      "Tài thuyết phục tuyệt vời — hợp lãnh đạo truyền cảm hứng.",
    ],
    love: [
      "Yêu nhanh, cuồng nhiệt — cần đối tác đủ bình tĩnh để cân bằng.",
      "Hợp người mệnh Mộc (Mộc sinh Hoả) hoặc cùng Hoả. Kỵ Thuỷ (Thuỷ khắc Hoả).",
      "Tránh ghen tuông bùng nổ — Hoả khi giận dễ thiêu rụi cả tốt lẫn xấu.",
    ],
    family: [
      "Mang lại không khí ấm áp, vui vẻ — gia đình quanh người mệnh Hoả thường rộn ràng.",
      "Phòng khách và phòng ăn nên đặt hướng Nam (cung Hoả).",
      "Bếp nấu hướng Nam, có ánh sáng tự nhiên — kích hoạt vận khí Hoả tích cực.",
    ],
    health: [
      "Lưu ý tim mạch và huyết áp — Hoả trong Đông y ứng với tim.",
      "Tránh nóng giận kéo dài — gan tim đều bị ảnh hưởng.",
      "Tập thể thao nhịp tim cao (chạy, đạp xe) tốt nhưng cần thư giãn xen kẽ.",
    ],
    finance: [
      "Hợp khởi nghiệp / dự án rủi ro cao lãi cao — Hoả không sợ thua nhanh.",
      "Tránh giữ tiền mặt nhiều — Hoả tính bốc, dễ tiêu sạch.",
      "Két sắt đặt hướng Nam; nên đầu tư đa dạng để cân bằng.",
    ],
  },
  Thổ: {
    career: [
      "Hợp bất động sản, xây dựng, gốm sứ, nông nghiệp truyền thống, hành chính, kế toán.",
      "Bền bỉ và đáng tin — phù hợp vị trí cần cam kết dài hạn, ít thay đổi.",
      "Tài quản lý tài sản hữu hình — không nhanh nhưng chắc.",
    ],
    love: [
      "Yêu chậm nhưng sâu — cần đối tác kiên nhẫn, không thúc ép.",
      "Hợp người mệnh Hoả (Hoả sinh Thổ) hoặc cùng Thổ. Kỵ Mộc (Mộc khắc Thổ).",
      "Trung thành cao — nhưng cần học nói lời yêu thương, vì Thổ ít biểu lộ.",
    ],
    family: [
      "Là điểm tựa vững chắc — gia đình quanh người mệnh Thổ ít biến động.",
      "Phòng thờ và phòng ngủ chính nên đặt hướng Tây Nam hoặc Đông Bắc (cung Thổ).",
      "Đồ gốm, đá tự nhiên trong nhà kích hoạt mạnh khí Thổ.",
    ],
    health: [
      "Lưu ý tiêu hoá và đường ruột — Thổ trong Đông y ứng với tỳ vị.",
      "Tránh ăn vội vàng và ăn không đúng giờ — dạ dày dễ tổn thương.",
      "Đi bộ và tập thể dục nhẹ đều mỗi ngày tốt hơn tập nặng cuối tuần.",
    ],
    finance: [
      "Hợp đầu tư bất động sản, vàng, tài sản hữu hình lâu dài.",
      "Tránh đầu cơ chứng khoán ngắn hạn — Thổ chậm, thua nhanh nhất ở thị trường biến.",
      "Két sắt đặt hướng Tây Nam hoặc Đông Bắc; ưu tiên giữ tài sản dạng vật chất.",
    ],
  },
};
